import type { EmailComposeAttachment } from '@elva/ui';
import type { EmailAttachment, EmailMessageDetail } from '../api/types';

export interface CachedSentAttachment {
  handle: string;
  filename: string;
  contentType?: string;
  previewUrl?: string;
}

const cacheByThread = new Map<string, CachedSentAttachment[]>();

function readAttachmentName(row: EmailAttachment): string {
  return (row.filename ?? row.name ?? '').trim();
}

function readAttachmentId(row: EmailAttachment): string | undefined {
  const wire = row as EmailAttachment & Record<string, unknown>;
  for (const key of ['attachment_id', 'id', 'attachmentId', 'handle', 'file_id', 'uuid']) {
    const raw = wire[key];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  }
  return undefined;
}

export function rememberSentAttachments(
  threadId: string,
  handles: string[],
  files: EmailComposeAttachment[],
): void {
  if (!threadId.trim() || !handles.length) return;

  const existing = cacheByThread.get(threadId) ?? [];
  const added = handles.map((handle, index) => {
    const file = files[index]?.file;
    const filename = file?.name ?? '';
    const contentType = file?.type.trim() || undefined;
    const previewUrl =
      file?.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
    return { handle, filename, ...(contentType ? { contentType } : {}), ...(previewUrl ? { previewUrl } : {}) };
  });

  cacheByThread.set(threadId, [...existing, ...added]);
}

/** Re-apply upload handles when the API returns filename-only outbound rows. */
export function enrichWithCachedSentAttachments(detail: EmailMessageDetail): EmailMessageDetail {
  const threadId = detail.id?.trim();
  if (!threadId || !detail.messages?.length) return detail;

  const cached = cacheByThread.get(threadId);
  if (!cached?.length) return detail;

  const remaining = [...cached];

  const messages = detail.messages.map((message) => {
    if (message.direction !== 'outbound' || !message.attachments?.length) return message;

    const attachments = message.attachments.map((row) => {
      if (readAttachmentId(row)) return row;

      const name = readAttachmentName(row);
      const matchIndex = remaining.findIndex((entry) => entry.filename === name);
      const match = matchIndex >= 0 ? remaining.splice(matchIndex, 1)[0] : remaining.shift();
      if (!match) return row;

      return {
        ...row,
        attachment_id: match.handle,
        ...(name ? { filename: name, name } : {}),
        ...(match.contentType ? { content_type: match.contentType, type: match.contentType } : {}),
        ...(match.previewUrl ? { preview_url: match.previewUrl } : {}),
      };
    });

    return { ...message, attachments };
  });

  return { ...detail, messages };
}
